/**
 * SubstrateDashboard
 *
 * Dashboard tab showing summary cards, P&L, break-even indicator,
 * and active inductions overview for the substrate operation.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useMemo } from 'react'
import { Loader2, TrendingUp, TrendingDown, Target, Package, Layers, Scissors, PoundSterling, AlertTriangle, Truck } from 'lucide-react'
import BatchEconomicsCard from './BatchEconomicsCard'

const STATUS_COLOURS = {
  inducted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  fruiting: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  harvesting: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  resting: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  spent: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
}

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short'
  })
}

export default function SubstrateDashboard({ hook, periodStart, periodEnd, onPeriodChange, onNavigateToInductions }) {
  const { profitability, totalLogsInStorage, activeInductions, inductions, shipments, purchases, overheads, runningCosts, costShares, isLoading } = hook

  // GMCo's net costs — use cost-share ratios to determine GMCo's portion
  const costShareAdjustment = useMemo(() => {
    if (costShares.length === 0) return { overheadReduction: 0, shipmentReduction: 0 }

    // Period overheads by category
    const periodOverheads = overheads.filter(o =>
      o.period_start <= periodEnd && o.period_end >= periodStart
    )

    let overheadReduction = 0
    const categories = [...new Set(costShares.map(cs => cs.overhead_category))]
    for (const category of categories) {
      const categoryTotal = periodOverheads
        .filter(o => o.category === category)
        .reduce((sum, o) => sum + Number(o.amount || 0), 0)
      const partnerShare = costShares
        .filter(cs => cs.overhead_category === category && cs.effective_from <= periodEnd && (!cs.effective_to || cs.effective_to >= periodStart))
        .reduce((sum, cs) => sum + Number(cs.share_ratio), 0)
      overheadReduction += categoryTotal * partnerShare
    }

    // Shipment costs are fully reimbursed by partners (100% transport share)
    const transportShare = costShares
      .filter(cs => cs.overhead_category === 'transport' && cs.effective_from <= periodEnd && (!cs.effective_to || cs.effective_to >= periodStart))
      .reduce((sum, cs) => sum + Number(cs.share_ratio), 0)
    // If transport share is 100%, all shipment costs are partner's
    const periodShipmentCosts = Number(profitability?.shipment_costs || 0)
    const shipmentReduction = periodShipmentCosts * transportShare

    return { overheadReduction, shipmentReduction, total: overheadReduction + shipmentReduction }
  }, [costShares, overheads, profitability, periodStart, periodEnd])

  // Stock depletion forecast
  const forecast = useMemo(() => {
    const LEAD_TIME_DAYS = 30
    const now = new Date()

    // Find the earliest activity date to calculate actual span
    const allDates = [
      ...(inductions || []).map(i => i.induction_date),
      ...(shipments || []).map(s => s.shipment_date)
    ].filter(Boolean).sort()

    if (allDates.length === 0) {
      return { weeklyBurnRate: 0, weeklyBurnGMCo: 0, weeklyBurnPartners: 0, weeksRemaining: Infinity, depletionDate: null, orderByDate: null, needsAlert: false, alertLevel: null }
    }

    // Use actual data span (minimum 1 week to avoid division spikes)
    const earliestDate = new Date(allDates[0] + 'T00:00:00')
    const daysSinceFirst = Math.max(7, Math.round((now - earliestDate) / (1000 * 60 * 60 * 24)))
    const weeksSinceFirst = daysSinceFirst / 7

    const totalInductionLogs = (inductions || [])
      .reduce((sum, i) => sum + (i.log_count || 0), 0)

    const totalShipmentLogs = (shipments || [])
      .reduce((sum, s) => sum + (s.log_count || 0), 0)

    const weeklyBurnRate = (totalInductionLogs + totalShipmentLogs) / weeksSinceFirst
    const weeklyBurnGMCo = totalInductionLogs / weeksSinceFirst
    const weeklyBurnPartners = totalShipmentLogs / weeksSinceFirst

    if (weeklyBurnRate <= 0) {
      return { weeklyBurnRate: 0, weeklyBurnGMCo: 0, weeklyBurnPartners: 0, weeksRemaining: Infinity, depletionDate: null, orderByDate: null, needsAlert: false, alertLevel: null }
    }

    const weeksRemaining = totalLogsInStorage / weeklyBurnRate
    const depletionDate = new Date(now)
    depletionDate.setDate(depletionDate.getDate() + Math.round(weeksRemaining * 7))
    const orderByDate = new Date(depletionDate)
    orderByDate.setDate(orderByDate.getDate() - LEAD_TIME_DAYS)

    const daysUntilOrder = Math.round((orderByDate - now) / (1000 * 60 * 60 * 24))
    const needsAlert = daysUntilOrder <= 14
    const alertLevel = daysUntilOrder <= 0 ? 'red' : daysUntilOrder <= 14 ? 'amber' : null

    return { weeklyBurnRate, weeklyBurnGMCo, weeklyBurnPartners, weeksRemaining, depletionDate, orderByDate, needsAlert, alertLevel }
  }, [inductions, shipments, totalLogsInStorage])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const p = profitability || {}

  // All-time summary figures (not period-filtered)
  const totalHarvestedKg = inductions.reduce((sum, i) => sum + Number(i.total_harvest_kg || 0), 0)
  const allTimeAvgPrice = Number(p.avg_price_per_kg || 0) // best available proxy

  const hasCostShares = costShares.length > 0
  const grossOverheads = Number(p.overhead_costs || 0)
  const grossShipmentCosts = Number(p.shipment_costs || 0)
  const grossRunningCosts = Number(p.running_costs || 0)
  const gmcoOverheads = grossOverheads - costShareAdjustment.overheadReduction
  const gmcoShipmentCosts = grossShipmentCosts - costShareAdjustment.shipmentReduction
  const gmcoTotalCosts = Number(p.log_costs || 0) + gmcoOverheads + gmcoShipmentCosts + grossRunningCosts
  const mushroomRevenue = Number(p.mushroom_revenue || p.revenue || 0)
  const blockRevenue = Number(p.block_revenue || 0)
  const blocksSold = Number(p.blocks_sold || 0)
  const totalRevenue = Number(p.revenue || 0)
  const profitLoss = totalRevenue - gmcoTotalCosts
  const isProfit = profitLoss >= 0
  const avgPricePerKg = Number(p.avg_price_per_kg || 0)
  // Block revenue offsets costs that need to be covered by mushroom sales
  const netCostsForBreakeven = Math.max(0, gmcoTotalCosts - blockRevenue)
  const breakevenKg = avgPricePerKg > 0 ? netCostsForBreakeven / avgPricePerKg : 0
  const kgSold = Number(p.kg_sold || 0)
  const kgToBreakeven = Math.max(0, breakevenKg - kgSold)

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input
            type="date"
            value={periodStart}
            onChange={(e) => onPeriodChange(e.target.value, periodEnd)}
            className="w-40"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input
            type="date"
            value={periodEnd}
            onChange={(e) => onPeriodChange(periodStart, e.target.value)}
            className="w-40"
          />
        </div>
      </div>

      {/* Stock Forecast + All Time Summary — side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Stock Forecast */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {forecast.needsAlert
                ? <AlertTriangle className={`h-5 w-5 ${forecast.alertLevel === 'red' ? 'text-red-600' : 'text-amber-500'}`} />
                : <Package className="h-5 w-5 text-muted-foreground" />
              }
              Stock Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            {forecast.weeklyBurnRate > 0 ? (
              <div className="space-y-4">
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Logs Remaining</span>
                    <span className="font-bold">{totalLogsInStorage}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Weekly Burn Rate</span>
                    <div className="text-right">
                      <div className="font-bold">~{forecast.weeklyBurnRate.toFixed(1)}/week</div>
                      <div className="text-xs text-muted-foreground">GMCo: {forecast.weeklyBurnGMCo.toFixed(1)} | Partners: {forecast.weeklyBurnPartners.toFixed(1)}</div>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estimated Depletion</span>
                    <div className="text-right">
                      <div className="font-bold">{forecast.depletionDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      <div className="text-xs text-muted-foreground">{Math.round(forecast.weeksRemaining)} weeks</div>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Order By</span>
                    <div className="text-right">
                      <div className={`font-bold ${forecast.alertLevel === 'red' ? 'text-red-600' : forecast.alertLevel === 'amber' ? 'text-amber-600' : ''}`}>
                        {forecast.orderByDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      <div className="text-xs text-muted-foreground">30-day lead time</div>
                    </div>
                  </div>
                </div>
                {forecast.needsAlert && (
                  <div className={`rounded-lg p-3 text-sm ${forecast.alertLevel === 'red' ? 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-300' : 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300'}`}>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      {forecast.alertLevel === 'red'
                        ? 'Order deadline has passed — contact your supplier immediately.'
                        : 'Order deadline approaching — place your order soon (30-day lead time).'
                      }
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No activity to calculate burn rate.
              </p>
            )}
          </CardContent>
        </Card>

        {/* All Time Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              All Time Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Logs in Storage</span>
                <span className="font-bold">{totalLogsInStorage}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shiitake Revenue</span>
                <span className="font-bold">£{(totalHarvestedKg * allTimeAvgPrice).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Harvested</span>
                <span className="font-bold">{totalHarvestedKg} kg</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Avg Price/kg</span>
                <span className="font-bold">£{allTimeAvgPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active Inductions</span>
                <span className="font-bold">{activeInductions.length}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* P&L Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isProfit ? <TrendingUp className="h-5 w-5 text-green-600" /> : <TrendingDown className="h-5 w-5 text-red-600" />}
            Profit & Loss
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-6">
            <div>
              <div className="text-sm text-muted-foreground">Mushroom Revenue</div>
              <div className="text-xl font-bold text-green-600">£{mushroomRevenue.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">{Number(p.kg_sold || 0).toFixed(1)} kg sold</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Block Revenue</div>
              <div className="text-xl font-bold text-green-600">£{blockRevenue.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">{blocksSold} blocks sold</div>
            </div>
            <div title={hasCostShares ? `Gross: £${Number(p.log_costs || 0).toFixed(2)}` : undefined}>
              <div className="text-sm text-muted-foreground">Log Costs</div>
              <div className="text-xl font-bold text-red-600">£{Number(p.log_costs || 0).toFixed(2)}</div>
            </div>
            <div title={hasCostShares ? `Gross: £${grossOverheads.toFixed(2)} · Partner share: £${costShareAdjustment.overheadReduction.toFixed(2)}` : undefined}>
              <div className="text-sm text-muted-foreground">Overheads</div>
              <div className="text-xl font-bold text-red-600">£{hasCostShares ? gmcoOverheads.toFixed(2) : grossOverheads.toFixed(2)}</div>
              {hasCostShares && <div className="text-xs text-muted-foreground">GMCo&apos;s share</div>}
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Running Costs</div>
              <div className="text-xl font-bold text-red-600">£{grossRunningCosts.toFixed(2)}</div>
            </div>
            <div title={hasCostShares ? `Gross: £${grossShipmentCosts.toFixed(2)} · Partner share: £${costShareAdjustment.shipmentReduction.toFixed(2)}` : undefined}>
              <div className="text-sm text-muted-foreground">Shipment Costs</div>
              <div className="text-xl font-bold text-red-600">£{hasCostShares ? gmcoShipmentCosts.toFixed(2) : grossShipmentCosts.toFixed(2)}</div>
              {hasCostShares && <div className="text-xs text-muted-foreground">GMCo&apos;s share</div>}
            </div>
            <div>
              <div className="text-sm text-muted-foreground">{hasCostShares ? 'Net Profit / Loss' : 'Profit / Loss'}</div>
              <div className={`text-xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                {isProfit ? '+' : ''}£{profitLoss.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Break-even indicator */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Break-even</span>
            </div>
            {breakevenKg > 0 ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {kgToBreakeven > 0
                      ? `${kgToBreakeven.toFixed(1)} kg more to break even`
                      : `Break-even achieved + ${(kgSold - breakevenKg).toFixed(1)} kg surplus`
                    }
                  </span>
                  <span className="font-medium">{breakevenKg.toFixed(1)} kg needed</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${kgToBreakeven > 0 ? 'bg-amber-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.min(100, (kgSold / breakevenKg) * 100)}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                No costs recorded for this period — add purchases and overheads to see break-even
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Batch Economics */}
      {purchases.filter(p => p.target_yield_percent).map(purchase => (
        <BatchEconomicsCard
          key={purchase.id}
          purchase={purchase}
          inductions={inductions}
          overheads={overheads}
          runningCosts={runningCosts}
          costShares={costShares}
          avgPricePerKg={Number(profitability?.avg_price_per_kg || 0)}
          weeklyBurnRate={forecast.weeklyBurnRate}
        />
      ))}

      {/* Active Inductions Table */}
      {activeInductions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Inductions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left py-2 font-medium">Code</th>
                    <th className="text-left py-2 font-medium">Inducted</th>
                    <th className="text-right py-2 font-medium">Logs</th>
                    <th className="text-center py-2 font-medium">Status</th>
                    <th className="text-right py-2 font-medium">Harvested</th>
                    <th className="text-right py-2 font-medium">BE%</th>
                    <th className="text-right py-2 font-medium">Yield/Log</th>
                    <th className="text-right py-2 font-medium">Income</th>
                    <th className="text-right py-2 font-medium">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {activeInductions.map(ind => {
                    const bePercent = ind.total_substrate_weight_kg > 0
                      ? ((ind.total_harvest_kg / ind.total_substrate_weight_kg) * 100).toFixed(1)
                      : '0.0'
                    const yieldPerLog = ind.log_count > 0
                      ? (ind.total_harvest_kg / ind.log_count).toFixed(3)
                      : '0'
                    // Income target: expected yield at target BE × avg selling price
                    const targetBE = Number(ind.substrate_log_purchases?.target_yield_percent || 25) / 100
                    const unitWeight = Number(ind.substrate_log_purchases?.unit_weight_kg || 2.1)
                    const expectedYield = ind.log_count * unitWeight * targetBE
                    const incomeTarget = expectedYield * Number(p.avg_price_per_kg || 0)
                    const actualIncome = ind.total_harvest_kg * Number(p.avg_price_per_kg || 0)

                    return (
                      <tr key={ind.id} className="border-b last:border-0">
                        <td className="py-2 font-mono">
                          <button
                            className="hover:underline cursor-pointer"
                            onClick={onNavigateToInductions}
                          >
                            {ind.induction_code}
                          </button>
                        </td>
                        <td className="py-2">{formatDate(ind.induction_date)}</td>
                        <td className="text-right py-2">{ind.log_count}</td>
                        <td className="text-center py-2">
                          <Badge className={STATUS_COLOURS[ind.status] || ''}>
                            {ind.status}
                          </Badge>
                        </td>
                        <td className="text-right py-2 font-medium">{ind.total_harvest_kg} kg</td>
                        <td className="text-right py-2">{bePercent}%</td>
                        <td className="text-right py-2">{yieldPerLog} kg</td>
                        <td className="text-right py-2 font-medium">
                          {actualIncome > 0 ? `£${actualIncome.toFixed(0)}` : '—'}
                        </td>
                        <td className="text-right py-2">
                          <span>£{incomeTarget.toFixed(0)}</span>
                          {actualIncome > 0 && (
                            <span className={`text-xs ml-1 ${actualIncome >= incomeTarget ? 'text-green-600' : 'text-muted-foreground'}`}>
                              ({((actualIncome / incomeTarget) * 100).toFixed(0)}%)
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
